import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { api } from '../../api/client';
import { toast } from 'sonner';
import {
  ShoppingCart,
  Package,
  DollarSign,
  TrendingUp,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Plus,
  Save,
  Trash2,
  RefreshCw,
  Check,
  Search,
  Ship,
  Truck,
  Factory,
  ArrowRight,
  Calculator,
  FileText,
  BarChart3,
  Clock,
  ChevronDown,
  ChevronUp,
  X,
  AlertTriangle,
  Info,
  Anchor
} from 'lucide-react';

// ============ DELETE CONFIRMATION MODAL ============
const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, project }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      
      {/* Modal */}
      <div className="relative bg-slate-800 border border-slate-700 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
            <Trash2 className="w-5 h-5 text-red-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-2">Delete Buying Project?</h3>
            <p className="text-sm text-gray-400 mb-4">
              Are you sure you want to delete <span className="text-white font-medium">{project?.project_id}: {project?.project_name}</span>? 
              This action cannot be undone.
            </p>
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-300 bg-slate-700 hover:bg-slate-600 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-500 rounded transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// ============ EDITABLE INPUT COMPONENT ============
// Uses local state to prevent cursor jumping, updates parent on blur
const EditableInput = ({ value, onChange, type = 'text', className = '', placeholder = '', step, ...props }) => {
  const [localValue, setLocalValue] = useState(value ?? '');
  const inputRef = useRef(null);
  
  // Sync with external value only when it changes from outside (not during typing)
  useEffect(() => {
    setLocalValue(value ?? '');
  }, [value]);

  const handleChange = (e) => {
    setLocalValue(e.target.value);
  };

  const handleBlur = () => {
    // Parse value based on type
    let finalValue = localValue;
    if (type === 'number') {
      finalValue = step && step < 1 ? parseFloat(localValue) || 0 : parseInt(localValue) || 0;
    }
    onChange(finalValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur(); // Trigger blur to save
    }
  };

  return (
    <input
      ref={inputRef}
      type={type}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={className}
      placeholder={placeholder}
      step={step}
      {...props}
    />
  );
};

// ============ SHIPMENT COST PANEL COMPONENT ============
const ShipmentCostPanel = ({ project, setProject, templates, formatCurrency, calculateProjectTotals }) => {
  const [expanded, setExpanded] = useState(true);
  const shipmentCosts = project?.shipment_costs || {};
  const products = project?.products || [];
  const totalBuyQty = products.reduce((sum, p) => sum + (p.buy_qty || 0), 0);

  // Update shipment cost field
  const updateShipmentCost = (field, value) => {
    setProject(prev => {
      const newShipmentCosts = {
        ...(prev.shipment_costs || {}),
        [field]: parseFloat(value) || 0
      };
      return { ...prev, shipment_costs: newShipmentCosts };
    });
  };

  // Load template
  const loadTemplate = (template) => {
    if (!template) {
      // Reset to blank
      setProject(prev => ({
        ...prev,
        shipment_costs: {
          ocean_freight_total: 0,
          customs_rate_pct: 0,
          forwarding_total: 0,
          drayage_total: 0,
          insurance_total: 0,
          packaging_per_unit: 0,
          tpl_storage_per_unit: 0,
          tpl_pick_pack_per_unit: 0,
          tpl_shipping_per_unit: 0,
          template_id: ""
        }
      }));
    } else {
      setProject(prev => ({
        ...prev,
        shipment_costs: {
          ocean_freight_total: template.ocean_freight_total || 0,
          customs_rate_pct: template.customs_rate_pct || 52,
          forwarding_total: template.forwarding_total || 0,
          drayage_total: template.drayage_total || 0,
          insurance_total: template.insurance_total || 0,
          packaging_per_unit: template.packaging_per_unit || 0.50,
          tpl_storage_per_unit: template.tpl_storage_per_unit || 0.50,
          tpl_pick_pack_per_unit: template.tpl_pick_pack_per_unit || 2.50,
          tpl_shipping_per_unit: template.tpl_shipping_per_unit || 4.20,
          template_id: template._id || "",
          source_notes: template.source_notes || ""
        }
      }));
    }
  };

  // Calculate summary values
  const totalFreightImport = (shipmentCosts.ocean_freight_total || 0) + 
    (shipmentCosts.forwarding_total || 0) + 
    (shipmentCosts.drayage_total || 0) + 
    (shipmentCosts.insurance_total || 0);
  const totalPackaging = (shipmentCosts.packaging_per_unit || 0) * totalBuyQty;
  const shippingCostPerUnit = totalBuyQty > 0 ? totalFreightImport / totalBuyQty : 0;
  const tplCostPerUnit = (shipmentCosts.tpl_storage_per_unit || 0) + 
    (shipmentCosts.tpl_pick_pack_per_unit || 0) + 
    (shipmentCosts.tpl_shipping_per_unit || 0);

  return (
    <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
      {/* Header */}
      <div 
        className="p-3 bg-slate-900 flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-blue-400" />
          <div>
            <span className="text-sm font-semibold text-white">📦 Shipment-Level Costs</span>
            <span className="text-[10px] text-gray-400 ml-2">Enter total costs → auto-allocates per unit to each product row below</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-400">Load Template:</span>
            <select
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                const val = e.target.value;
                if (val === 'blank') {
                  loadTemplate(null);
                } else {
                  const template = templates.find(t => t._id === val);
                  if (template) loadTemplate(template);
                }
              }}
              className="bg-slate-700 border border-slate-600 rounded px-2 py-1 text-xs text-white"
              value={shipmentCosts.template_id || ''}
            >
              <option value="">Select template...</option>
              {templates.map(t => (
                <option key={t._id} value={t._id}>{t.template_name}</option>
              ))}
              <option value="blank">Custom / Blank</option>
            </select>
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {/* Body */}
      {expanded && (
        <div className="p-4">
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column - Freight & Import Costs */}
            <div>
              <h4 className="text-xs font-semibold text-blue-400 mb-3 flex items-center gap-2">
                <Ship className="w-4 h-4" />
                🚢 Freight & Import Costs
              </h4>
              <div className="space-y-2">
                <CostInputRow
                  label="Ocean Freight (total)"
                  value={shipmentCosts.ocean_freight_total || 0}
                  onChange={(v) => updateShipmentCost('ocean_freight_total', v)}
                  perUnit={totalBuyQty > 0 ? (shipmentCosts.ocean_freight_total || 0) / totalBuyQty : 0}
                  totalQty={totalBuyQty}
                  isTotal
                />
                <div className="flex items-center gap-2 py-2 border-t border-slate-700">
                  <span className="text-[10px] text-gray-400 w-36">Customs Duty Rate %</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-1">
                      <EditableInput
                        type="number"
                        value={shipmentCosts.customs_rate_pct || 52}
                        onChange={(v) => updateShipmentCost('customs_rate_pct', v)}
                        className="w-16 bg-slate-900 border border-red-500/50 rounded px-2 py-1 text-xs text-red-400 text-right font-semibold"
                        step="1"
                      />
                      <span className="text-red-400 text-xs">%</span>
                    </div>
                  </div>
                  <span className="text-[10px] text-red-400 font-semibold">⚠️ Applied to Mfg cost</span>
                </div>
                <CostInputRow
                  label="Forwarding & Handling"
                  value={shipmentCosts.forwarding_total || 0}
                  onChange={(v) => updateShipmentCost('forwarding_total', v)}
                  perUnit={totalBuyQty > 0 ? (shipmentCosts.forwarding_total || 0) / totalBuyQty : 0}
                  totalQty={totalBuyQty}
                  isTotal
                />
                <CostInputRow
                  label="Drayage (Port → Warehouse)"
                  value={shipmentCosts.drayage_total || 0}
                  onChange={(v) => updateShipmentCost('drayage_total', v)}
                  perUnit={totalBuyQty > 0 ? (shipmentCosts.drayage_total || 0) / totalBuyQty : 0}
                  totalQty={totalBuyQty}
                  isTotal
                />
                <CostInputRow
                  label="Insurance (total)"
                  value={shipmentCosts.insurance_total || 0}
                  onChange={(v) => updateShipmentCost('insurance_total', v)}
                  perUnit={totalBuyQty > 0 ? (shipmentCosts.insurance_total || 0) / totalBuyQty : 0}
                  totalQty={totalBuyQty}
                  isTotal
                />
                <CostInputRow
                  label="Packaging / Unit"
                  value={shipmentCosts.packaging_per_unit || 0.50}
                  onChange={(v) => updateShipmentCost('packaging_per_unit', v)}
                  perUnit={shipmentCosts.packaging_per_unit || 0}
                  totalQty={totalBuyQty}
                  isPerUnit
                />
              </div>
            </div>

            {/* Right Column - 3PL Fulfillment Costs */}
            <div>
              <h4 className="text-xs font-semibold text-green-400 mb-3 flex items-center gap-2">
                <Package className="w-4 h-4" />
                📦 3PL Fulfillment Costs (per unit)
              </h4>
              <div className="space-y-2">
                <CostInputRow
                  label="Storage / Unit / Month"
                  value={shipmentCosts.tpl_storage_per_unit || 0.50}
                  onChange={(v) => updateShipmentCost('tpl_storage_per_unit', v)}
                  perUnit={shipmentCosts.tpl_storage_per_unit || 0}
                  totalQty={totalBuyQty}
                  isPerUnit
                  color="green"
                />
                <CostInputRow
                  label="Pick + Pack / Unit"
                  value={shipmentCosts.tpl_pick_pack_per_unit || 2.50}
                  onChange={(v) => updateShipmentCost('tpl_pick_pack_per_unit', v)}
                  perUnit={shipmentCosts.tpl_pick_pack_per_unit || 0}
                  totalQty={totalBuyQty}
                  isPerUnit
                  color="green"
                />
                <CostInputRow
                  label="Outbound Shipping / Unit"
                  value={shipmentCosts.tpl_shipping_per_unit || 4.20}
                  onChange={(v) => updateShipmentCost('tpl_shipping_per_unit', v)}
                  perUnit={shipmentCosts.tpl_shipping_per_unit || 0}
                  totalQty={totalBuyQty}
                  isPerUnit
                  color="green"
                />
              </div>

              {/* Source Reference */}
              {shipmentCosts.source_notes && (
                <div className="mt-4 p-3 bg-slate-900 rounded border border-slate-700">
                  <div className="text-[10px] text-gray-400 mb-1">📄 Source Reference</div>
                  <div className="text-[10px] text-gray-300">{shipmentCosts.source_notes}</div>
                </div>
              )}

              {/* Tariff Alert */}
              {(shipmentCosts.customs_rate_pct || 0) > 25 && (
                <div className="mt-4 p-3 bg-red-500/10 rounded border border-red-500/30">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="text-[10px] text-red-300">
                      <strong>⚠️ Tariff Alert:</strong> {shipmentCosts.customs_rate_pct}% customs duty is your biggest cost — 
                      more than manufacturing for most SKUs. Check 2026 tariff status before finalizing.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Summary Strip */}
          <div className="mt-4 pt-3 border-t border-slate-700 grid grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-[10px] text-gray-400 uppercase">Total Freight & Import</div>
              <div className="text-lg font-bold text-red-400">{formatCurrency(totalFreightImport, true)}</div>
            </div>
            <div className="text-center">
              <div className="text-[10px] text-gray-400 uppercase">Total Packaging</div>
              <div className="text-lg font-bold text-amber-400">{formatCurrency(totalPackaging, true)}</div>
            </div>
            <div className="text-center">
              <div className="text-[10px] text-gray-400 uppercase">Shipping Cost / Unit</div>
              <div className="text-lg font-bold text-cyan-400">${shippingCostPerUnit.toFixed(2)}</div>
            </div>
            <div className="text-center">
              <div className="text-[10px] text-gray-400 uppercase">3PL Cost / Unit</div>
              <div className="text-lg font-bold text-green-400">${tplCostPerUnit.toFixed(2)}</div>
            </div>
          </div>
          <div className="mt-2 text-center text-[10px] text-gray-500">
            ↓ These per-unit costs auto-fill into the table below. You can still override individual cells per product.
          </div>
        </div>
      )}
    </div>
  );
};

// Helper component for cost input rows
const CostInputRow = ({ label, value, onChange, perUnit, totalQty, isTotal, isPerUnit, color = "blue" }) => {
  return (
    <div className="flex items-center gap-2 py-1">
      <span className="text-[10px] text-gray-400 w-36">{label}</span>
      <div className="flex-1 flex items-center gap-2">
        <span className="text-gray-500">$</span>
        <EditableInput
          type="number"
          value={value || ''}
          onChange={onChange}
          className={`w-20 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white text-right`}
          step="0.01"
        />
      </div>
      <div className={`text-[10px] ${color === 'green' ? 'text-green-400' : 'text-cyan-400'} w-28 text-right`}>
        {isTotal && totalQty > 0 && (
          <span>÷ {totalQty.toLocaleString()} = <strong>${perUnit.toFixed(2)}</strong>/unit</span>
        )}
        {isPerUnit && (
          <span><strong>${perUnit.toFixed(2)}</strong>/unit</span>
        )}
      </div>
    </div>
  );
};

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const BuyingPlanTab = ({ tenantId = 'tenant-123' }) => {
  const [view, setView] = useState('portfolio'); // 'portfolio' or 'detail'
  const [projects, setProjects] = useState([]);
  const [portfolio, setPortfolio] = useState({});
  const [cashflowData, setCashflowData] = useState([]);
  const [timelineData, setTimelineData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [availableVendors, setAvailableVendors] = useState([]);
  const [costTemplates, setCostTemplates] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, project: null });

  // Default shipment costs
  const defaultShipmentCosts = {
    ocean_freight_total: 0,
    customs_rate_pct: 52,
    forwarding_total: 0,
    drayage_total: 0,
    insurance_total: 0,
    packaging_per_unit: 0.50,
    tpl_storage_per_unit: 0.50,
    tpl_pick_pack_per_unit: 2.50,
    tpl_shipping_per_unit: 4.20,
    template_id: ""
  };

  const year = 2026;
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Status colors
  const statusColors = {
    draft: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    planning: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    ordered: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    manufacturing: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    shipped: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    in_customs: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    received: 'bg-green-500/20 text-green-400 border-green-500/30',
    selling: 'bg-green-500/20 text-green-400 border-green-500/30',
    completed: 'bg-green-500/20 text-green-400 border-green-500/30'
  };

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [projectsRes, portfolioRes, cashflowRes, timelineRes, productsRes, vendorsRes, templatesRes] = await Promise.all([
        api.get(`/api/v1/buying-plan/projects?tenant_id=${tenantId}&year=${year}`),
        api.get(`/api/v1/buying-plan/portfolio?tenant_id=${tenantId}&year=${year}`),
        api.get(`/api/v1/buying-plan/portfolio/cashflow?tenant_id=${tenantId}&year=${year}`),
        api.get(`/api/v1/buying-plan/portfolio/timeline?tenant_id=${tenantId}&year=${year}`),
        api.get(`/api/v1/buying-plan/products?tenant_id=${tenantId}&year=${year}`),
        api.get(`/api/v1/buying-plan/vendors?tenant_id=${tenantId}`),
        api.get(`/api/v1/buying-plan/templates?tenant_id=${tenantId}`)
      ]);

      setProjects(projectsRes.data?.projects || projectsRes.projects || []);
      setPortfolio(projectsRes.data?.portfolio || projectsRes.portfolio || {});
      setCashflowData(cashflowRes.data?.monthly_cashflow || cashflowRes.monthly_cashflow || []);
      setTimelineData(timelineRes.data?.timeline || timelineRes.timeline || []);
      setAvailableProducts(productsRes.data?.products || productsRes.products || []);
      setAvailableVendors(vendorsRes.data?.vendors || vendorsRes.vendors || []);
      setCostTemplates(templatesRes.data?.templates || templatesRes.templates || []);
    } catch (err) {
      console.error('Error loading buying plan data:', err);
      toast.error('Failed to load buying plan data');
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (value, compact = false) => {
    if (!value && value !== 0) return '—';
    if (compact) {
      return value >= 1000 ? `$${(value / 1000).toFixed(1)}K` : `$${value.toFixed(0)}`;
    }
    return `$${value.toFixed(2)}`;
  };

  // Create new project
  const createNewProject = async () => {
    try {
      const res = await api.post(`/api/v1/buying-plan/projects?tenant_id=${tenantId}`, {
        project_name: 'New Buying Project',
        year: year,
        manufacturer: { name: '', location: '', origin_port: 'Mumbai, India' },
        products: [],
        shipment: { container_type: '20ft', destination: 'Warren, MI' },
        timeline: { order_date: '', manufacturing_days: 14, port_loading_days: 3, ocean_transit_days: 28, customs_days: 5, drayage_days: 2, receiving_days: 1 },
        cash_flow: { events: [], total_investment: 0, expected_revenue: 0, expected_profit: 0 }
      });

      const newProject = res.data?.project || res.project;
      setSelectedProject(newProject);
      setCurrentStep(1);
      setView('detail');
      toast.success('Project created');
      loadData();
    } catch (err) {
      toast.error('Failed to create project');
    }
  };

  // Save project
  const saveProject = async () => {
    if (!selectedProject) return;
    setSaving(true);
    try {
      const projectId = selectedProject.id || selectedProject.project_id;
      await api.put(`/api/v1/buying-plan/projects/${projectId}?tenant_id=${tenantId}`, selectedProject);
      toast.success('Project saved');
      loadData();
    } catch (err) {
      toast.error('Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  // Delete project - opens confirmation modal
  const confirmDeleteProject = (project) => {
    setDeleteModal({ open: true, project });
  };

  // Execute delete after confirmation
  const executeDelete = async () => {
    const project = deleteModal.project;
    if (!project) return;
    
    try {
      await api.delete(`/api/v1/buying-plan/projects/${project.project_id}?tenant_id=${tenantId}`);
      toast.success('Project deleted');
      setDeleteModal({ open: false, project: null });
      if (view === 'detail') {
        setView('portfolio');
        setSelectedProject(null);
      }
      loadData();
    } catch (err) {
      toast.error('Failed to delete project');
    }
  };

  // Calculate project totals
  const calculateProjectTotals = (project) => {
    if (!project?.products) return project;
    
    let totalUnits = 0;
    let totalInvestment = 0;
    let totalRevenue = 0;
    let totalPallets = 0;
    let totalCbm = 0;

    const updatedProducts = project.products.map(p => {
      const costs = p.costs || {};
      
      // Calculate landed cost
      const landedCost = (costs.manufacturing_per_unit || 0) + 
                        (costs.ocean_freight_per_unit || 0) + 
                        (costs.customs_duties_per_unit || 0) + 
                        (costs.freight_forwarding_per_unit || 0) + 
                        (costs.packaging_per_unit || 0);
      
      // Calculate total cost
      const totalCost = landedCost + 
                       (costs.tpl_storage_per_unit || 0) + 
                       (costs.tpl_pick_pack_per_unit || 0) + 
                       (costs.tpl_shipping_per_unit || 0);
      
      // Calculate margins
      const sellingPrice = costs.selling_price || 0;
      const marginDollar = sellingPrice - totalCost;
      const marginPercent = sellingPrice > 0 ? (marginDollar / sellingPrice) * 100 : 0;
      
      // Calculate investment
      const buyQty = p.buy_qty || 0;
      const investment = buyQty * landedCost;
      
      // Calculate pallet info
      const palletInfo = p.pallet_info || {};
      const unitsPerPallet = (palletInfo.units_per_carton || 12) * (palletInfo.cartons_per_pallet || 48);
      const palletsNeeded = buyQty > 0 ? Math.ceil(buyQty / unitsPerPallet) : 0;
      const cbm = palletsNeeded * 1.2;
      
      totalUnits += buyQty;
      totalInvestment += investment;
      totalRevenue += buyQty * sellingPrice;
      totalPallets += palletsNeeded;
      totalCbm += cbm;

      return {
        ...p,
        costs: {
          ...costs,
          landed_cost_per_unit: landedCost,
          total_cost_per_unit: totalCost,
          margin_dollar: marginDollar,
          margin_percent: marginPercent,
          total_investment: investment
        },
        pallet_info: {
          ...palletInfo,
          units_per_pallet: unitsPerPallet,
          pallets_needed: palletsNeeded,
          cbm: cbm
        }
      };
    });

    // Calculate shipping cost per unit
    const shipment = project.shipment || {};
    const totalShippingCost = (shipment.container_cost || 0) + 
                              (shipment.customs_total || 0) + 
                              (shipment.duties_total || 0) + 
                              (shipment.freight_forwarding || 0) + 
                              (shipment.drayage || 0) + 
                              (shipment.insurance || 0) + 
                              (shipment.other_charges || 0);
    const costPerUnit = totalUnits > 0 ? totalShippingCost / totalUnits : 0;

    // Calculate cash flow
    const totalCOGS = updatedProducts.reduce((sum, p) => sum + ((p.buy_qty || 0) * (p.costs?.total_cost_per_unit || 0)), 0);
    const expectedProfit = totalRevenue - totalCOGS;
    const blendedMargin = totalRevenue > 0 ? (expectedProfit / totalRevenue) * 100 : 0;
    const roi = totalInvestment > 0 ? (expectedProfit / totalInvestment) * 100 : 0;

    return {
      ...project,
      products: updatedProducts,
      shipment: {
        ...shipment,
        total_shipping_cost: totalShippingCost,
        cost_per_unit: costPerUnit,
        total_pallets: totalPallets,
        total_cbm: totalCbm
      },
      cash_flow: {
        ...project.cash_flow,
        total_investment: totalInvestment + totalShippingCost,
        expected_revenue: totalRevenue,
        expected_profit: expectedProfit,
        blended_margin_percent: blendedMargin,
        roi_percent: roi
      }
    };
  };

  // Update project field (text fields don't need recalculation)
  const updateProject = (path, value, skipRecalc = false) => {
    setSelectedProject(prev => {
      const updated = { ...prev };
      const keys = path.split('.');
      let current = updated;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      
      // Skip recalculation for text fields like project_name, manufacturer.name, etc.
      if (skipRecalc) {
        return updated;
      }
      return calculateProjectTotals(updated);
    });
  };

  // Update simple text field without recalculation
  const updateProjectText = (path, value) => {
    updateProject(path, value, true);
  };

  // ============ PORTFOLIO VIEW ============
  const PortfolioView = () => (
    <div className="space-y-4" data-testid="portfolio-view">
      {/* KPI Cards */}
      <div className="grid grid-cols-5 gap-3" data-testid="portfolio-kpi-cards">
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3" data-testid="kpi-cash-needed">
          <div className="text-[10px] text-red-400 uppercase">Total Cash Needed</div>
          <div className="text-xl font-bold text-red-400">{formatCurrency(portfolio.total_investment, true)}</div>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3" data-testid="kpi-cash-deployed">
          <div className="text-[10px] text-amber-400 uppercase">Cash Deployed</div>
          <div className="text-xl font-bold text-amber-400">{formatCurrency(portfolio.cash_deployed, true)}</div>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3" data-testid="kpi-expected-revenue">
          <div className="text-[10px] text-blue-400 uppercase">Expected Revenue</div>
          <div className="text-xl font-bold text-blue-400">{formatCurrency(portfolio.expected_revenue, true)}</div>
        </div>
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3" data-testid="kpi-expected-profit">
          <div className="text-[10px] text-green-400 uppercase">Expected Profit</div>
          <div className="text-xl font-bold text-green-400">{formatCurrency(portfolio.expected_profit, true)}</div>
        </div>
        <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3" data-testid="kpi-active-projects">
          <div className="text-[10px] text-cyan-400 uppercase">Active Projects</div>
          <div className="text-xl font-bold text-cyan-400">{projects.length}</div>
          <div className="text-[9px] text-gray-500 mt-1">
            {Object.entries(portfolio.status_counts || {}).map(([s, c]) => `${c} ${s}`).join(' · ')}
          </div>
        </div>
      </div>

      {/* Project List */}
      <div className="bg-slate-800 rounded-lg border border-slate-700" data-testid="projects-list-container">
        <div className="p-3 border-b border-slate-700 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <Package className="w-4 h-4" />
            Buying Projects — {year}
          </h3>
          <button
            onClick={createNewProject}
            data-testid="new-buying-project-btn"
            className="flex items-center gap-1 px-3 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-500 rounded transition-colors"
          >
            <Plus className="w-3 h-3" />
            New Buying Project
          </button>
        </div>

        {projects.length === 0 ? (
          <div className="p-8 text-center" data-testid="empty-projects-state">
            <ShoppingCart className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-sm text-gray-400 mb-4">No buying projects yet</p>
            <button
              onClick={createNewProject}
              data-testid="create-first-project-btn"
              className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-500 rounded"
            >
              Create Your First Project
            </button>
          </div>
        ) : (
          <div className="divide-y divide-slate-700">
            {projects.map(project => {
              const productCount = project.products?.length || 0;
              const totalUnits = project.products?.reduce((sum, p) => sum + (p.buy_qty || 0), 0) || 0;
              const cf = project.cash_flow || {};
              
              return (
                <div
                  key={project.id || project.project_id}
                  className="p-3 hover:bg-slate-700/50 cursor-pointer transition-colors"
                  onClick={() => {
                    setSelectedProject(project);
                    setCurrentStep(1);
                    setView('detail');
                  }}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs font-mono rounded">
                        {project.project_id}
                      </span>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white truncate">{project.project_name}</div>
                      <div className="text-[10px] text-gray-500">
                        {project.timeline?.expected_ship_date || '—'} → {project.timeline?.expected_arrival_date || '—'} · {project.shipment?.container_type || '20ft'}
                      </div>
                    </div>
                    
                    <div className="text-xs text-gray-400 w-[100px]">
                      <div>{project.manufacturer?.name || '—'}</div>
                      <div className="text-[10px] text-gray-500">{project.manufacturer?.location || ''}</div>
                    </div>
                    
                    <div className="text-xs text-gray-300 w-[80px] text-center">
                      <div>{productCount} SKUs</div>
                      <div className="text-[10px] text-gray-500">{totalUnits.toLocaleString()} units</div>
                    </div>
                    
                    <div className="text-xs text-red-400 w-[80px] text-right font-semibold">
                      {formatCurrency(cf.total_investment, true)}
                    </div>
                    
                    <div className="text-xs text-blue-400 w-[80px] text-right">
                      {formatCurrency(cf.expected_revenue, true)}
                    </div>
                    
                    <div className="text-xs text-green-400 w-[60px] text-right font-semibold">
                      {cf.blended_margin_percent?.toFixed(1) || 0}%
                    </div>
                    
                    <div className="flex-shrink-0">
                      <span className={`px-2 py-1 text-[10px] rounded border ${statusColors[project.status] || statusColors.draft}`}>
                        {project.status}
                      </span>
                    </div>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmDeleteProject(project);
                      }}
                      className="p-1 text-gray-500 hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Timeline Gantt */}
      {timelineData.length > 0 && (
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
          <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {year} Procurement Timeline — All Projects
          </h3>
          
          <div className="overflow-x-auto">
            <div className="min-w-[800px]">
              {/* Month headers */}
              <div className="flex items-center mb-2">
                <div className="w-[200px] text-xs text-gray-400">Project</div>
                {MONTHS.map((m, i) => (
                  <div key={m} className="flex-1 text-center text-[10px] text-gray-500">{m}</div>
                ))}
              </div>
              
              {/* Project rows */}
              {timelineData.map((item, idx) => (
                <div key={idx} className="flex items-center h-8 mb-1">
                  <div className="w-[200px] text-xs text-gray-300 truncate pr-2">{item.project_name}</div>
                  <div className="flex-1 flex relative h-6">
                    {/* Manufacturing bar (purple) */}
                    {item.manufacturing?.start && item.manufacturing?.end && (
                      <div
                        className="absolute h-full bg-purple-500/60 rounded"
                        style={{
                          left: `${((item.manufacturing.start - 1) / 12) * 100}%`,
                          width: `${((item.manufacturing.end - item.manufacturing.start + 1) / 12) * 100}%`
                        }}
                      />
                    )}
                    {/* Transit bar (blue) */}
                    {item.transit?.start && item.transit?.end && (
                      <div
                        className="absolute h-full bg-blue-500/60 rounded"
                        style={{
                          left: `${((item.transit.start - 1) / 12) * 100}%`,
                          width: `${((item.transit.end - item.transit.start + 1) / 12) * 100}%`
                        }}
                      />
                    )}
                    {/* Selling bar (green) */}
                    {item.selling?.start && (
                      <div
                        className="absolute h-full bg-green-500/60 rounded"
                        style={{
                          left: `${((item.selling.start - 1) / 12) * 100}%`,
                          width: `${((item.selling.end - item.selling.start + 1) / 12) * 100}%`
                        }}
                      />
                    )}
                  </div>
                </div>
              ))}
              
              {/* Legend */}
              <div className="flex items-center gap-4 mt-3 text-[10px]">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-purple-500/60 rounded" />
                  <span className="text-gray-400">Manufacturing</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-blue-500/60 rounded" />
                  <span className="text-gray-400">Transit</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-green-500/60 rounded" />
                  <span className="text-gray-400">Selling</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cash Flow Chart */}
      {cashflowData.length > 0 && (
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
          <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            {year} Combined Cash Flow — All Projects
          </h3>
          
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={cashflowData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="month_name" stroke="#9ca3af" fontSize={10} />
              <YAxis stroke="#9ca3af" fontSize={10} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                formatter={(value) => [`$${value.toFixed(0)}`, '']}
              />
              <Legend />
              <Bar dataKey="cash_out" fill="#ef4444" name="Cash Out" />
              <Bar dataKey="cash_in" fill="#22c55e" name="Cash In" />
            </BarChart>
          </ResponsiveContainer>
          
          <div className="mt-3 p-2 bg-green-500/10 border border-green-500/30 rounded text-center">
            <span className="text-xs text-green-400">
              🎯 All projects: {formatCurrency(portfolio.total_investment, true)} invested → {formatCurrency(portfolio.expected_revenue, true)} revenue → {formatCurrency(portfolio.expected_profit, true)} profit ({portfolio.roi_percent?.toFixed(0) || 0}% ROI)
            </span>
          </div>
        </div>
      )}
    </div>
  );

  // ============ PROJECT DETAIL VIEW ============
  const ProjectDetailView = () => {
    const [productSearch, setProductSearch] = useState('');
    const [showAllProducts, setShowAllProducts] = useState(false);
    const [selectedSkus, setSelectedSkus] = useState(new Set(
      (selectedProject?.products || []).map(p => p.sku)
    ));

    const steps = [
      { num: 1, label: 'Select Products', icon: ShoppingCart },
      { num: 2, label: 'Unit Economics', icon: Calculator },
      { num: 3, label: 'Shipment & Containers', icon: Ship },
      { num: 4, label: 'Cash Flow & ROI', icon: DollarSign }
    ];

    // Bug 2 Fix: Sort products to show selected first, then filter by search
    const sortedAndFilteredProducts = useMemo(() => {
      const filtered = availableProducts.filter(p =>
        p.sku?.toLowerCase().includes(productSearch.toLowerCase()) ||
        p.name?.toLowerCase().includes(productSearch.toLowerCase())
      );
      
      // If project has products, show selected first
      if (selectedSkus.size > 0 && !showAllProducts && !productSearch) {
        // Return only selected products by default
        return filtered.filter(p => selectedSkus.has(p.sku));
      }
      
      // Sort: selected products first, then unselected
      return filtered.sort((a, b) => {
        const aSelected = selectedSkus.has(a.sku);
        const bSelected = selectedSkus.has(b.sku);
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        return 0;
      });
    }, [availableProducts, productSearch, selectedSkus, showAllProducts]);

    // Toggle product selection
    const toggleProduct = (product) => {
      const newSelected = new Set(selectedSkus);
      if (newSelected.has(product.sku)) {
        newSelected.delete(product.sku);
      } else {
        newSelected.add(product.sku);
      }
      setSelectedSkus(newSelected);

      // Update project products
      const newProducts = [];
      newSelected.forEach(sku => {
        const existing = selectedProject?.products?.find(p => p.sku === sku);
        if (existing) {
          newProducts.push(existing);
        } else {
          const prod = availableProducts.find(p => p.sku === sku);
          if (prod) {
            newProducts.push({
              sku: prod.sku,
              product_name: prod.name,
              buy_qty: prod.forecast_qty || 0,
              forecast_qty: prod.forecast_qty || 0,
              costs: {
                selling_price: prod.selling_price || 0,
                manufacturing_per_unit: 0,
                ocean_freight_per_unit: 0,
                customs_duties_per_unit: 0,
                freight_forwarding_per_unit: 0,
                packaging_per_unit: 0,
                tpl_storage_per_unit: 0,
                tpl_pick_pack_per_unit: 0,
                tpl_shipping_per_unit: 0
              },
              pallet_info: {
                units_per_carton: 12,
                cartons_per_pallet: 48
              }
            });
          }
        }
      });

      setSelectedProject(prev => calculateProjectTotals({ ...prev, products: newProducts }));
    };

    // Bug 3 Fix: Update product cost only on blur (called from EditableInput)
    const updateProductCost = useCallback((sku, field, value) => {
      setSelectedProject(prev => {
        const updatedProducts = (prev.products || []).map(p => {
          if (p.sku === sku) {
            return {
              ...p,
              costs: { ...p.costs, [field]: parseFloat(value) || 0 }
            };
          }
          return p;
        });
        return calculateProjectTotals({ ...prev, products: updatedProducts });
      });
    }, []);

    // Bug 3 Fix: Update product field only on blur (called from EditableInput)
    const updateProductField = useCallback((sku, field, value) => {
      setSelectedProject(prev => {
        const updatedProducts = (prev.products || []).map(p => {
          if (p.sku === sku) {
            return { ...p, [field]: parseInt(value) || 0 };
          }
          return p;
        });
        return calculateProjectTotals({ ...prev, products: updatedProducts });
      });
    }, []);

    // Update pallet info
    const updatePalletInfo = useCallback((sku, field, value) => {
      setSelectedProject(prev => {
        const updatedProducts = (prev.products || []).map(p => {
          if (p.sku === sku) {
            return {
              ...p,
              pallet_info: { ...p.pallet_info, [field]: parseInt(value) || 0 }
            };
          }
          return p;
        });
        return calculateProjectTotals({ ...prev, products: updatedProducts });
      });
    }, []);

    const totalSelectedUnits = (selectedProject?.products || []).reduce((sum, p) => sum + (p.buy_qty || 0), 0);

    return (
      <div className="space-y-4" data-testid="project-detail-view">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setView('portfolio')}
              data-testid="back-to-portfolio-btn"
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-white"
            >
              <ChevronLeft className="w-4 h-4" />
              All Projects
            </button>
            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs font-mono rounded" data-testid="project-id-badge">
              {selectedProject?.project_id}
            </span>
            {/* Bug 1 Fix: Use EditableInput for project name */}
            <EditableInput
              type="text"
              value={selectedProject?.project_name || ''}
              onChange={(value) => updateProjectText('project_name', value)}
              data-testid="project-name-input"
              className="bg-transparent text-lg font-semibold text-white border-b border-transparent hover:border-slate-600 focus:border-blue-500 focus:outline-none px-1 min-w-[200px]"
              placeholder="Project Name"
            />
            <span className={`px-2 py-1 text-[10px] rounded border ${statusColors[selectedProject?.status] || statusColors.draft}`} data-testid="project-status-badge">
              {selectedProject?.status}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={saveProject}
              disabled={saving}
              data-testid="save-project-btn"
              className="flex items-center gap-1 px-3 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-500 rounded disabled:opacity-50"
            >
              {saving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Save Project
            </button>
          </div>
        </div>

        {/* Meta Fields - Manufacturer as searchable dropdown */}
        <div className="grid grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] text-gray-400 uppercase block mb-1">Manufacturer</label>
            <div className="relative">
              <input
                type="text"
                list="vendor-list"
                value={selectedProject?.manufacturer?.name || ''}
                onChange={(e) => {
                  const selectedVendor = availableVendors.find(v => v.name === e.target.value);
                  updateProjectText('manufacturer.name', e.target.value);
                  if (selectedVendor?.location) {
                    updateProjectText('manufacturer.location', selectedVendor.location);
                  }
                }}
                className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-xs text-white"
                placeholder="Select or type manufacturer..."
              />
              <datalist id="vendor-list">
                {availableVendors.map((v) => (
                  <option key={v.name} value={v.name}>
                    {v.location && `(${v.location})`}
                  </option>
                ))}
              </datalist>
            </div>
          </div>
          <div>
            <label className="text-[10px] text-gray-400 uppercase block mb-1">Location</label>
            <EditableInput
              type="text"
              value={selectedProject?.manufacturer?.location || ''}
              onChange={(value) => updateProjectText('manufacturer.location', value)}
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-xs text-white"
              placeholder="e.g., Mumbai, India"
            />
          </div>
          <div>
            <label className="text-[10px] text-gray-400 uppercase block mb-1">Origin Port</label>
            <EditableInput
              type="text"
              value={selectedProject?.manufacturer?.origin_port || ''}
              onChange={(value) => updateProjectText('manufacturer.origin_port', value)}
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-xs text-white"
              placeholder="e.g., Mumbai, India"
            />
          </div>
          <div>
            <label className="text-[10px] text-gray-400 uppercase block mb-1">Status</label>
            <select
              value={selectedProject?.status || 'draft'}
              onChange={(e) => updateProject('status', e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-xs text-white"
            >
              <option value="draft">Draft</option>
              <option value="planning">Planning</option>
              <option value="ordered">Ordered</option>
              <option value="manufacturing">Manufacturing</option>
              <option value="shipped">Shipped</option>
              <option value="in_customs">In Customs</option>
              <option value="received">Received at 3PL</option>
              <option value="selling">Selling</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        {/* Step Flow */}
        <div className="flex items-center justify-between bg-slate-800 rounded-lg p-2">
          {steps.map((step, idx) => (
            <React.Fragment key={step.num}>
              <button
                onClick={() => setCurrentStep(step.num)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded transition-colors ${
                  currentStep === step.num
                    ? 'bg-blue-600 text-white'
                    : currentStep > step.num
                    ? 'text-green-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {currentStep > step.num ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <span className="w-5 h-5 rounded-full border flex items-center justify-center text-[10px]">
                    {step.num}
                  </span>
                )}
                <span className="text-xs">{step.label}</span>
              </button>
              {idx < steps.length - 1 && <ArrowRight className="w-4 h-4 text-gray-600" />}
            </React.Fragment>
          ))}
        </div>

        {/* Step Content */}
        {currentStep === 1 && (
          <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4" />
                  Select Products for This Project
                </h3>
                <p className="text-[10px] text-gray-400 mt-1">
                  Select products and set buy quantities. Forecast from 2026 projection.
                </p>
              </div>
              
              {/* Toggle tabs for Selected vs All */}
              <div className="flex items-center gap-1 bg-slate-900 rounded p-1">
                <button
                  onClick={() => setShowAllProducts(false)}
                  className={`px-3 py-1 text-xs rounded transition-colors ${
                    !showAllProducts ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Selected ({selectedSkus.size})
                </button>
                <button
                  onClick={() => setShowAllProducts(true)}
                  className={`px-3 py-1 text-xs rounded transition-colors ${
                    showAllProducts ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  All Products ({availableProducts.length})
                </button>
              </div>
            </div>

            {/* Search */}
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 rounded pl-8 pr-3 py-2 text-xs text-white placeholder-gray-500"
                  placeholder="Search by SKU or product name..."
                />
              </div>
            </div>

            {/* Compact Product Table */}
            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-900 z-10">
                  <tr>
                    <th className="px-2 py-2 text-left text-gray-400 w-10">✓</th>
                    <th className="px-2 py-2 text-left text-gray-400 w-24">SKU</th>
                    <th className="px-2 py-2 text-left text-gray-400">Product Name</th>
                    <th className="px-2 py-2 text-right text-amber-400 w-28">2026 Forecast</th>
                    <th className="px-2 py-2 text-right text-blue-400 w-24">Buy Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAndFilteredProducts.map((product, idx) => {
                    const isSelected = selectedSkus.has(product.sku);
                    const projectProduct = selectedProject?.products?.find(p => p.sku === product.sku);
                    const buyQty = projectProduct?.buy_qty || 0;
                    
                    return (
                      <tr 
                        key={product.sku}
                        className={`border-b border-slate-700/50 ${
                          isSelected ? 'bg-green-500/10' : idx % 2 === 0 ? 'bg-slate-800/30' : ''
                        }`}
                      >
                        <td className="px-2 py-2">
                          <button
                            onClick={() => toggleProduct(product)}
                            className={`w-5 h-5 rounded border flex items-center justify-center ${
                              isSelected ? 'bg-green-500 border-green-500' : 'border-gray-600 hover:border-gray-400'
                            }`}
                          >
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </button>
                        </td>
                        <td className="px-2 py-2 text-blue-400 font-mono">{product.sku}</td>
                        <td className="px-2 py-2 text-gray-300 truncate max-w-[300px]">{product.name}</td>
                        <td className="px-2 py-2 text-right text-amber-400">
                          {(product.forecast_qty || 0).toLocaleString()} units
                        </td>
                        <td className="px-2 py-2 text-right">
                          {isSelected ? (
                            <EditableInput
                              type="number"
                              value={buyQty}
                              onChange={(value) => updateProductField(product.sku, 'buy_qty', value)}
                              className="w-20 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-right text-white"
                            />
                          ) : (
                            <span className="text-gray-600">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {sortedAndFilteredProducts.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center py-8 text-gray-500">
                        {productSearch ? 'No products match your search' : showAllProducts ? 'No products available' : 'No products selected yet'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer with correct labels */}
            <div className="mt-4 pt-3 border-t border-slate-700 flex items-center justify-between">
              <div className="text-sm text-gray-300">
                <strong className="text-white">{selectedSkus.size} products</strong> selected · 
                Total forecast: <strong className="text-amber-400">{(selectedProject?.products || []).reduce((sum, p) => sum + (p.forecast_qty || 0), 0).toLocaleString()}</strong> units ·
                Total buy qty: <strong className="text-blue-400">{totalSelectedUnits.toLocaleString()}</strong> units
              </div>
              <button
                onClick={() => setCurrentStep(2)}
                className="flex items-center gap-1 px-3 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-500 rounded"
              >
                Next: Unit Economics
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="space-y-4">
            {/* Shipment Cost Panel */}
            <ShipmentCostPanel 
              project={selectedProject}
              setProject={setSelectedProject}
              templates={costTemplates}
              formatCurrency={formatCurrency}
              calculateProjectTotals={calculateProjectTotals}
            />

            {/* Unit Economics Table */}
            <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Calculator className="w-4 h-4" />
                  {selectedProject?.project_id} Per-SKU Unit Economics
                </h3>
                <div className="text-[10px] text-gray-400">
                  ↓ Costs auto-filled from Shipment Panel. You can override individual cells.
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-[10px]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  {/* Column Group Headers */}
                  <thead>
                    <tr>
                      <th colSpan="3" className="px-2 py-1 text-left text-gray-500 text-[9px] uppercase"></th>
                      <th colSpan="7" className="px-2 py-1 text-center text-blue-400 text-[9px] uppercase border-b-2 border-blue-500/50 bg-blue-500/10">
                        India Manufacturing & Freight
                      </th>
                      <th colSpan="4" className="px-2 py-1 text-center text-green-400 text-[9px] uppercase border-b-2 border-green-500/50 bg-green-500/10">
                        3PL Fulfillment
                      </th>
                      <th colSpan="4" className="px-2 py-1 text-center text-purple-400 text-[9px] uppercase border-b-2 border-purple-500/50 bg-purple-500/10">
                        Margin Analysis
                      </th>
                    </tr>
                    <tr className="bg-slate-900">
                      <th className="px-2 py-2 text-left text-gray-400">SKU</th>
                      <th className="px-2 py-2 text-left text-gray-400">Product</th>
                      <th className="px-2 py-2 text-right text-gray-400">Buy Qty</th>
                      <th className="px-2 py-2 text-right text-blue-400 border-l border-blue-500/30">Mfg</th>
                      <th className="px-2 py-2 text-right text-blue-400">Ocean</th>
                      <th className="px-2 py-2 text-right text-red-400">Customs</th>
                      <th className="px-2 py-2 text-right text-blue-400">Fwd</th>
                      <th className="px-2 py-2 text-right text-blue-400">Dray</th>
                      <th className="px-2 py-2 text-right text-blue-400">Pkg</th>
                      <th className="px-2 py-2 text-right text-cyan-400 font-semibold">LANDED</th>
                      <th className="px-2 py-2 text-right text-green-400 border-l border-green-500/30">Storage</th>
                      <th className="px-2 py-2 text-right text-green-400">Pick+Pk</th>
                      <th className="px-2 py-2 text-right text-green-400">Ship</th>
                      <th className="px-2 py-2 text-right text-cyan-400 font-semibold">TOTAL</th>
                      <th className="px-2 py-2 text-right text-purple-400 border-l border-purple-500/30">Sell $</th>
                      <th className="px-2 py-2 text-right text-green-400">Margin $</th>
                      <th className="px-2 py-2 text-right text-green-400">%</th>
                      <th className="px-2 py-2 text-right text-cyan-400">Invest</th>
                    </tr>
                  </thead>
                <tbody>
                  {(selectedProject?.products || []).map((p, idx) => {
                    const costs = p.costs || {};
                    const shipmentCosts = selectedProject?.shipment_costs || {};
                    const totalBuyQty = (selectedProject?.products || []).reduce((sum, pr) => sum + (pr.buy_qty || 0), 0);
                    
                    // Calculate per-unit costs (auto-allocated from shipment costs)
                    const oceanPerUnit = !costs.ocean_per_unit_override && totalBuyQty > 0 
                      ? (shipmentCosts.ocean_freight_total || 0) / totalBuyQty 
                      : (costs.ocean_freight_per_unit || 0);
                    const customsPerUnit = !costs.customs_per_unit_override
                      ? (costs.manufacturing_per_unit || 0) * ((shipmentCosts.customs_rate_pct || 0) / 100)
                      : (costs.customs_duties_per_unit || 0);
                    const fwdPerUnit = !costs.fwd_per_unit_override && totalBuyQty > 0
                      ? (shipmentCosts.forwarding_total || 0) / totalBuyQty
                      : (costs.freight_forwarding_per_unit || 0);
                    const drayPerUnit = !costs.dray_per_unit_override && totalBuyQty > 0
                      ? (shipmentCosts.drayage_total || 0) / totalBuyQty
                      : (costs.drayage_per_unit || 0);
                    const pkgPerUnit = !costs.pkg_per_unit_override
                      ? (shipmentCosts.packaging_per_unit || 0)
                      : (costs.packaging_per_unit || 0);
                    
                    const landedCost = (costs.manufacturing_per_unit || 0) + oceanPerUnit + customsPerUnit + fwdPerUnit + drayPerUnit + pkgPerUnit;
                    
                    const storagePerUnit = shipmentCosts.tpl_storage_per_unit || costs.tpl_storage_per_unit || 0;
                    const pickPackPerUnit = shipmentCosts.tpl_pick_pack_per_unit || costs.tpl_pick_pack_per_unit || 0;
                    const shipPerUnit = shipmentCosts.tpl_shipping_per_unit || costs.tpl_shipping_per_unit || 0;
                    
                    const totalCost = landedCost + storagePerUnit + pickPackPerUnit + shipPerUnit;
                    const marginDollars = (costs.selling_price || 0) - totalCost;
                    const marginPercent = costs.selling_price > 0 ? (marginDollars / costs.selling_price) * 100 : 0;
                    const totalInvestment = (p.buy_qty || 0) * landedCost;
                    
                    return (
                      <tr key={p.sku} className={`border-b border-slate-700 ${idx % 2 === 0 ? 'bg-slate-800/50' : ''}`}>
                        <td className="px-2 py-1.5 text-blue-400 font-mono">{p.sku}</td>
                        <td className="px-2 py-1.5 text-gray-300 max-w-[120px] truncate">{p.product_name}</td>
                        <td className="px-2 py-1.5 text-right">
                          <EditableInput
                            type="number"
                            value={p.buy_qty || ''}
                            onChange={(value) => updateProductField(p.sku, 'buy_qty', value)}
                            className="w-14 bg-slate-900 border border-slate-700 rounded px-1 py-0.5 text-right text-white"
                          />
                        </td>
                        <td className="px-2 py-1.5 text-right border-l border-slate-700">
                          <EditableInput
                            type="number"
                            value={costs.manufacturing_per_unit || ''}
                            onChange={(value) => updateProductCost(p.sku, 'manufacturing_per_unit', value)}
                            className="w-12 bg-slate-900 border border-slate-700 rounded px-1 py-0.5 text-right text-white"
                            step="0.01"
                          />
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <div className="flex flex-col items-end">
                            <span className="text-blue-300">${oceanPerUnit.toFixed(2)}</span>
                            {!costs.ocean_per_unit_override && <span className="text-[8px] text-gray-500">÷ qty</span>}
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <div className="flex flex-col items-end">
                            <span className="text-red-400 font-semibold">${customsPerUnit.toFixed(2)}</span>
                            {!costs.customs_per_unit_override && <span className="text-[8px] text-red-400/60">{shipmentCosts.customs_rate_pct || 52}%</span>}
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <div className="flex flex-col items-end">
                            <span className="text-blue-300">${fwdPerUnit.toFixed(2)}</span>
                            {!costs.fwd_per_unit_override && <span className="text-[8px] text-gray-500">÷ qty</span>}
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <div className="flex flex-col items-end">
                            <span className="text-blue-300">${drayPerUnit.toFixed(2)}</span>
                            {!costs.dray_per_unit_override && <span className="text-[8px] text-gray-500">÷ qty</span>}
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <div className="flex flex-col items-end">
                            <span className="text-blue-300">${pkgPerUnit.toFixed(2)}</span>
                            {!costs.pkg_per_unit_override && <span className="text-[8px] text-gray-500">uniform</span>}
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-right text-cyan-400 font-semibold">
                          ${landedCost.toFixed(2)}
                        </td>
                        <td className="px-2 py-1.5 text-right border-l border-slate-700 text-green-400">
                          ${storagePerUnit.toFixed(2)}
                        </td>
                        <td className="px-2 py-1.5 text-right text-green-400">
                          ${pickPackPerUnit.toFixed(2)}
                        </td>
                        <td className="px-2 py-1.5 text-right text-green-400">
                          ${shipPerUnit.toFixed(2)}
                        </td>
                        <td className="px-2 py-1.5 text-right text-cyan-400 font-semibold">
                          ${totalCost.toFixed(2)}
                        </td>
                        <td className="px-2 py-1.5 text-right border-l border-slate-700">
                          <EditableInput
                            type="number"
                            value={costs.selling_price || ''}
                            onChange={(value) => updateProductCost(p.sku, 'selling_price', value)}
                            className="w-14 bg-slate-900 border border-slate-700 rounded px-1 py-0.5 text-right text-white"
                            step="0.01"
                          />
                        </td>
                        <td className={`px-2 py-1.5 text-right font-semibold ${marginDollars >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          ${marginDollars.toFixed(2)}
                        </td>
                        <td className={`px-2 py-1.5 text-right ${marginPercent >= 30 ? 'text-green-400' : marginPercent >= 15 ? 'text-amber-400' : 'text-red-400'}`}>
                          {marginPercent.toFixed(1)}%
                        </td>
                        <td className="px-2 py-1.5 text-right text-cyan-400">
                          ${totalInvestment.toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-900 font-semibold">
                    <td colSpan="2" className="px-2 py-2 text-white">TOTAL BUY QTY</td>
                    <td className="px-2 py-2 text-right text-white">
                      {(selectedProject?.products || []).reduce((sum, p) => sum + (p.buy_qty || 0), 0).toLocaleString()}
                    </td>
                    <td colSpan="7" />
                    <td colSpan="3" />
                    <td />
                    <td />
                    <td />
                    <td className="px-2 py-2 text-right text-green-400">
                      {(() => {
                        const products = selectedProject?.products || [];
                        const shipmentCosts = selectedProject?.shipment_costs || {};
                        const totalBuyQty = products.reduce((sum, p) => sum + (p.buy_qty || 0), 0);
                        
                        let totalRev = 0;
                        let totalCostSum = 0;
                        
                        products.forEach(p => {
                          const costs = p.costs || {};
                          const oceanPerUnit = totalBuyQty > 0 ? (shipmentCosts.ocean_freight_total || 0) / totalBuyQty : 0;
                          const customsPerUnit = (costs.manufacturing_per_unit || 0) * ((shipmentCosts.customs_rate_pct || 0) / 100);
                          const fwdPerUnit = totalBuyQty > 0 ? (shipmentCosts.forwarding_total || 0) / totalBuyQty : 0;
                          const drayPerUnit = totalBuyQty > 0 ? (shipmentCosts.drayage_total || 0) / totalBuyQty : 0;
                          const pkgPerUnit = shipmentCosts.packaging_per_unit || 0;
                          const landedCost = (costs.manufacturing_per_unit || 0) + oceanPerUnit + customsPerUnit + fwdPerUnit + drayPerUnit + pkgPerUnit;
                          const tplCost = (shipmentCosts.tpl_storage_per_unit || 0) + (shipmentCosts.tpl_pick_pack_per_unit || 0) + (shipmentCosts.tpl_shipping_per_unit || 0);
                          const totalCost = landedCost + tplCost;
                          
                          totalRev += (p.buy_qty || 0) * (costs.selling_price || 0);
                          totalCostSum += (p.buy_qty || 0) * totalCost;
                        });
                        
                        return totalRev > 0 ? `${((totalRev - totalCostSum) / totalRev * 100).toFixed(1)}%` : '—';
                      })()}
                    </td>
                    <td className="px-2 py-2 text-right text-cyan-400">
                      {formatCurrency((() => {
                        const products = selectedProject?.products || [];
                        const shipmentCosts = selectedProject?.shipment_costs || {};
                        const totalBuyQty = products.reduce((sum, p) => sum + (p.buy_qty || 0), 0);
                        
                        let totalInv = 0;
                        products.forEach(p => {
                          const costs = p.costs || {};
                          const oceanPerUnit = totalBuyQty > 0 ? (shipmentCosts.ocean_freight_total || 0) / totalBuyQty : 0;
                          const customsPerUnit = (costs.manufacturing_per_unit || 0) * ((shipmentCosts.customs_rate_pct || 0) / 100);
                          const fwdPerUnit = totalBuyQty > 0 ? (shipmentCosts.forwarding_total || 0) / totalBuyQty : 0;
                          const drayPerUnit = totalBuyQty > 0 ? (shipmentCosts.drayage_total || 0) / totalBuyQty : 0;
                          const pkgPerUnit = shipmentCosts.packaging_per_unit || 0;
                          const landedCost = (costs.manufacturing_per_unit || 0) + oceanPerUnit + customsPerUnit + fwdPerUnit + drayPerUnit + pkgPerUnit;
                          totalInv += (p.buy_qty || 0) * landedCost;
                        });
                        
                        return totalInv;
                      })(), true)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

              <div className="mt-3 p-2 bg-slate-900 rounded text-[10px] text-gray-400">
                💡 <strong>Landed Cost</strong> = Mfg + Ocean + Customs + Fwd + Dray + Pkg &nbsp;|&nbsp; 
                <strong>Total Cost</strong> = Landed + Storage + Pick&Pack + Shipping &nbsp;|&nbsp; 
                <strong>Margin</strong> = Sell Price − Total Cost
              </div>

              <div className="mt-4 flex justify-between">
                <button onClick={() => setCurrentStep(1)} className="flex items-center gap-1 text-xs text-gray-400 hover:text-white">
                  <ChevronLeft className="w-4 h-4" /> Products
                </button>
                <button onClick={() => setCurrentStep(3)} className="flex items-center gap-1 px-3 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-500 rounded">
                  Shipment Planning <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="space-y-4">
            {/* Container Planning */}
            <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
              <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                <Ship className="w-4 h-4" />
                Container & Pallet Planning
              </h3>

              <div className="grid grid-cols-3 gap-3 mb-4">
                {['20ft', '40ft', 'LCL'].map(type => (
                  <div
                    key={type}
                    onClick={() => updateProject('shipment.container_type', type)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedProject?.shipment?.container_type === type
                        ? 'bg-blue-500/20 border-blue-500/50'
                        : 'bg-slate-900 border-slate-700 hover:border-slate-600'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Ship className="w-4 h-4 text-blue-400" />
                      <span className="text-sm font-semibold text-white">{type === 'LCL' ? 'LCL (Shared)' : `${type} Container`}</span>
                    </div>
                    <div className="text-[10px] text-gray-400 mt-1">
                      {type === '20ft' ? '~22 pallets' : type === '40ft' ? '~44 pallets' : 'Per CBM pricing'}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pallet Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-900">
                    <tr>
                      <th className="px-2 py-2 text-left text-gray-400">SKU</th>
                      <th className="px-2 py-2 text-left text-gray-400">Product</th>
                      <th className="px-2 py-2 text-right text-gray-400">Buy Qty</th>
                      <th className="px-2 py-2 text-right text-gray-400">Units/Carton</th>
                      <th className="px-2 py-2 text-right text-gray-400">Cartons/Pallet</th>
                      <th className="px-2 py-2 text-right text-cyan-400">Units/Pallet</th>
                      <th className="px-2 py-2 text-right text-cyan-400">Pallets</th>
                      <th className="px-2 py-2 text-right text-cyan-400">CBM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedProject?.products || []).map((p, idx) => {
                      const pi = p.pallet_info || {};
                      return (
                        <tr key={p.sku} className={`border-b border-slate-700 ${idx % 2 === 0 ? 'bg-slate-800/50' : ''}`}>
                          <td className="px-2 py-1.5 text-blue-400 font-mono">{p.sku}</td>
                          <td className="px-2 py-1.5 text-gray-300 max-w-[150px] truncate">{p.product_name}</td>
                          <td className="px-2 py-1.5 text-right text-white">{p.buy_qty}</td>
                          <td className="px-2 py-1.5 text-right">
                            <input
                              type="number"
                              value={pi.units_per_carton || 12}
                              onChange={(e) => updatePalletInfo(p.sku, 'units_per_carton', e.target.value)}
                              className="w-14 bg-slate-900 border border-slate-700 rounded px-1 py-0.5 text-right text-white"
                            />
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            <input
                              type="number"
                              value={pi.cartons_per_pallet || 48}
                              onChange={(e) => updatePalletInfo(p.sku, 'cartons_per_pallet', e.target.value)}
                              className="w-14 bg-slate-900 border border-slate-700 rounded px-1 py-0.5 text-right text-white"
                            />
                          </td>
                          <td className="px-2 py-1.5 text-right text-cyan-400">{pi.units_per_pallet || 0}</td>
                          <td className="px-2 py-1.5 text-right text-cyan-400 font-semibold">{pi.pallets_needed || 0}</td>
                          <td className="px-2 py-1.5 text-right text-cyan-400">{(pi.cbm || 0).toFixed(1)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-900 font-semibold">
                      <td colSpan="6" className="px-2 py-2 text-white">TOTAL</td>
                      <td className="px-2 py-2 text-right text-cyan-400">
                        {selectedProject?.shipment?.total_pallets || 0}
                      </td>
                      <td className="px-2 py-2 text-right text-cyan-400">
                        {(selectedProject?.shipment?.total_cbm || 0).toFixed(1)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Recommendation */}
              {(() => {
                const pallets = selectedProject?.shipment?.total_pallets || 0;
                const containerType = selectedProject?.shipment?.container_type || '20ft';
                const maxPallets = containerType === '20ft' ? 22 : containerType === '40ft' ? 44 : 999;
                const fits = pallets <= maxPallets;

                return (
                  <div className={`mt-3 p-2 rounded ${fits ? 'bg-green-500/10 border border-green-500/30' : 'bg-amber-500/10 border border-amber-500/30'}`}>
                    <span className={`text-xs ${fits ? 'text-green-400' : 'text-amber-400'}`}>
                      {fits
                        ? `✅ ${pallets} pallets fits in a ${containerType} container with room to spare.`
                        : `⚠️ ${pallets} pallets exceeds ${containerType} capacity (${maxPallets}). Consider a larger container.`}
                    </span>
                  </div>
                );
              })()}
            </div>

            {/* Shipping Costs */}
            <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
              <h3 className="text-sm font-semibold text-white mb-4">Shipping Cost Summary</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  {[
                    { key: 'container_cost', label: 'Container Cost' },
                    { key: 'customs_total', label: 'Customs & Import Duties' },
                    { key: 'freight_forwarding', label: 'Freight Forwarding Fee' },
                    { key: 'drayage', label: 'Drayage (port to warehouse)' },
                    { key: 'insurance', label: 'Insurance' },
                    { key: 'other_charges', label: 'Other Charges' }
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">{item.label}</span>
                      <input
                        type="number"
                        value={selectedProject?.shipment?.[item.key] || ''}
                        onChange={(e) => updateProject(`shipment.${item.key}`, parseFloat(e.target.value) || 0)}
                        className="w-24 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white text-right"
                        placeholder="$0.00"
                      />
                    </div>
                  ))}
                </div>
                
                <div className="bg-slate-900 rounded-lg p-3">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">Total Shipping & Import</span>
                      <span className="text-sm font-bold text-white">
                        ${(selectedProject?.shipment?.total_shipping_cost || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-slate-700">
                      <span className="text-xs text-gray-400">Cost per unit</span>
                      <span className="text-sm font-bold text-cyan-400">
                        ${(selectedProject?.shipment?.cost_per_unit || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
              <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Shipment Timeline
              </h3>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-[10px] text-gray-400 block mb-1">Order Date</label>
                  <input
                    type="date"
                    value={selectedProject?.timeline?.order_date || ''}
                    onChange={(e) => updateProject('timeline.order_date', e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-white"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-gray-400 block mb-1">Expected Ship Date</label>
                  <input
                    type="date"
                    value={selectedProject?.timeline?.expected_ship_date || ''}
                    onChange={(e) => updateProject('timeline.expected_ship_date', e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-white"
                  />
                </div>
              </div>

              {/* Visual Timeline */}
              <div className="flex items-center gap-1 mb-3">
                {[
                  { label: 'Mfg', days: selectedProject?.timeline?.manufacturing_days || 14, color: 'bg-purple-500' },
                  { label: 'Port', days: selectedProject?.timeline?.port_loading_days || 3, color: 'bg-indigo-500' },
                  { label: 'Ocean', days: selectedProject?.timeline?.ocean_transit_days || 28, color: 'bg-blue-500' },
                  { label: 'Customs', days: selectedProject?.timeline?.customs_days || 5, color: 'bg-amber-500' },
                  { label: 'Drayage', days: selectedProject?.timeline?.drayage_days || 2, color: 'bg-green-500' },
                  { label: 'Recv', days: selectedProject?.timeline?.receiving_days || 1, color: 'bg-cyan-500' }
                ].map((stage, idx) => (
                  <div key={idx} className="flex-1 text-center">
                    <div className={`h-6 ${stage.color} rounded flex items-center justify-center`}>
                      <span className="text-[9px] text-white font-semibold">{stage.days}d</span>
                    </div>
                    <span className="text-[9px] text-gray-400">{stage.label}</span>
                  </div>
                ))}
              </div>

              <div className="text-xs text-gray-400">
                Total Lead Time: ~{
                  (selectedProject?.timeline?.manufacturing_days || 14) +
                  (selectedProject?.timeline?.port_loading_days || 3) +
                  (selectedProject?.timeline?.ocean_transit_days || 28) +
                  (selectedProject?.timeline?.customs_days || 5) +
                  (selectedProject?.timeline?.drayage_days || 2) +
                  (selectedProject?.timeline?.receiving_days || 1)
                } days
              </div>
            </div>

            <div className="flex justify-between">
              <button onClick={() => setCurrentStep(2)} className="flex items-center gap-1 text-xs text-gray-400 hover:text-white">
                <ChevronLeft className="w-4 h-4" /> Unit Economics
              </button>
              <button onClick={() => setCurrentStep(4)} className="flex items-center gap-1 px-3 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-500 rounded">
                Cash Flow & ROI <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {currentStep === 4 && (
          <div className="space-y-4">
            {/* KPI Cards */}
            <div className="grid grid-cols-4 gap-3">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <div className="text-[10px] text-red-400 uppercase">Cash Needed</div>
                <div className="text-xl font-bold text-red-400">
                  {formatCurrency(selectedProject?.cash_flow?.total_investment, true)}
                </div>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <div className="text-[10px] text-blue-400 uppercase">Expected Revenue</div>
                <div className="text-xl font-bold text-blue-400">
                  {formatCurrency(selectedProject?.cash_flow?.expected_revenue, true)}
                </div>
              </div>
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                <div className="text-[10px] text-green-400 uppercase">Net Profit</div>
                <div className="text-xl font-bold text-green-400">
                  {formatCurrency(selectedProject?.cash_flow?.expected_profit, true)}
                </div>
              </div>
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                <div className="text-[10px] text-purple-400 uppercase">ROI</div>
                <div className="text-xl font-bold text-purple-400">
                  {(selectedProject?.cash_flow?.roi_percent || 0).toFixed(0)}%
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-4">
              {/* Cash Outlay */}
              <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  💸 When Cash Goes Out
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-400">Manufacturing (50% deposit)</span>
                    <span className="text-red-400">
                      -${((selectedProject?.products || []).reduce((sum, p) => sum + (p.buy_qty || 0) * (p.costs?.manufacturing_per_unit || 0), 0) / 2).toFixed(0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-400">Container Booking</span>
                    <span className="text-red-400">-${(selectedProject?.shipment?.container_cost || 0).toFixed(0)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-400">Manufacturing (50% balance)</span>
                    <span className="text-red-400">
                      -${((selectedProject?.products || []).reduce((sum, p) => sum + (p.buy_qty || 0) * (p.costs?.manufacturing_per_unit || 0), 0) / 2).toFixed(0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-400">Customs + Duties + Forwarding</span>
                    <span className="text-red-400">
                      -${((selectedProject?.shipment?.customs_total || 0) + (selectedProject?.shipment?.freight_forwarding || 0) + (selectedProject?.shipment?.drayage || 0)).toFixed(0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs pt-2 border-t border-slate-700 font-semibold">
                    <span className="text-white">Total Investment</span>
                    <span className="text-red-400">{formatCurrency(selectedProject?.cash_flow?.total_investment)}</span>
                  </div>
                </div>
              </div>

              {/* Expected Return */}
              <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  📈 Expected Return
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-400">Gross Revenue</span>
                    <span className="text-blue-400">{formatCurrency(selectedProject?.cash_flow?.expected_revenue)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-400">All-in COGS</span>
                    <span className="text-red-400">
                      -${((selectedProject?.products || []).reduce((sum, p) => sum + (p.buy_qty || 0) * (p.costs?.total_cost_per_unit || 0), 0)).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs pt-2 border-t border-slate-700 font-semibold">
                    <span className="text-white">Net Profit</span>
                    <span className="text-green-400">{formatCurrency(selectedProject?.cash_flow?.expected_profit)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm pt-2 border-t border-slate-700">
                    <span className="text-white font-semibold">ROI on Cash</span>
                    <span className="text-green-400 text-xl font-bold">
                      {(selectedProject?.cash_flow?.roi_percent || 0).toFixed(0)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button onClick={() => setCurrentStep(3)} className="flex items-center gap-1 text-xs text-gray-400 hover:text-white">
                <ChevronLeft className="w-4 h-4" /> Shipment
              </button>
              <button
                onClick={async () => {
                  await saveProject();
                  setView('portfolio');
                }}
                className="flex items-center gap-1 px-4 py-2 text-xs text-white bg-green-600 hover:bg-green-500 rounded"
              >
                <Save className="w-4 h-4" />
                Save & Close Project
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <>
      {view === 'portfolio' ? <PortfolioView /> : <ProjectDetailView />}
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, project: null })}
        onConfirm={executeDelete}
        project={deleteModal.project}
      />
    </>
  );
};

export default BuyingPlanTab;
